import styled from "styled-components";
import Flex from "../../../../designComponents/Flex";
import Typography from "../../../../designComponents/Typography";
import { humanReadableDate } from "../../../../utils/date";
import AmenitiesViewer from "./AmenitiesViewer";
import BookingConfigViewer from "./BookingConfigViewer";
import GroupImageViewer from "./GroupImageViewer";
import { InventoryCategoryViewer } from "./InventoryCategoryViewer";
import { InventoryViewer } from "./InventoryViewer";
import PropertyAdditionalQuestionsViewer from "./PropertyAdditionalQuestionsViewer";
import PropertyAddressViewer from "./PropertyAddressViewer";
import PropertyBasicInformationViewer from "./PropertyBasicInformationViewer";
import PropertyDescriptionViewer from "./PropertyDescriptionViewer";
import PropertyImagesViewer from "./PropertyImagesViewer";
import PropertyPricingViewer from "./PropertyPricingViewer";
import RulesViewer from "./RulesViewer";
import {
  IInventoryCategoryType,
  IPropertyChangeLog,
  IRule,
  IUtilityCategoryType,
} from "./types";
import { UtilityProviderViewer } from "./UtilityProviderViewer";

type Props = {
  title: string;
  date: string;
  changeLog: IPropertyChangeLog;
};

export const ChangeLogSection = ({ changeLog, date, title }: Props) => {
  const changes = changeLog?.changes;
  const entity = changeLog?.entityType;
  const entity2 = changeLog?.entityType2;
  const isPropertyWhole = entity === "Property" && !!changeLog?.property;
  const isAmenities =
    !isPropertyWhole && entity === "Property" && changes?.amenities;
  const isRefImageGroups =
    entity === "ImageReferenceGroup" && changes?.refImageGroups;
  const isRules = entity2 === "RulesCategory" && changes?.rules;
  const isInventoryCategory =
    entity === "InventoryCategory" && changes?.inventoryCategory;
  const isInventory = entity === "Inventory" && changes?.inventory;
  const isUtility = entity === "UtilityProvider" && changes?.utilityProvider;
  const utilityCategory = isUtility
    ? changeLog.changes2?.utilityProviderCategoryIcon?.find(
        (c: IUtilityCategoryType) =>
          c._id === changes?.utilityProvider?.category
      )
    : undefined;

  const isImages = entity === "Property" && changes?.images;
  const isDescription = entity === "Property" && changes?.description;
  const isAdditionalQuestions =
    entity === "Property" && changes?.additionalQuestions;
  const isBookingConfig = entity === "Property" && changes?.bookingConfig;
  const isPricing = entity === "Property" && changes?.pricing;
  const isBasicInformation =
    entity === "Property" && changes?.propertyType && changes?.furnishedType;

  const isSnapshot = !!changeLog.currentSnapshot;

  const renderSnapShot = () => {
    const property = changeLog.property;
    return (
      <Flex direction="column" style={{ margin: "20px 0px" }}>
        <Typography fontWeight="bold">Property Snapshot</Typography>
        <Flex
          direction="column"
          gap={"30px"}
          padding={"20px"}
          style={{
            backgroundColor: "rgba(255,255,255,0.5)",
            borderRadius: "20px",
          }}
        >
          {property && (
            <PropertyAddressViewer
              address={property.address}
              latitude={property?.location?.coordinates[1] || 0}
              longitude={property?.location?.coordinates[0] || 0}
            />
          )}
          {property && <PropertyBasicInformationViewer {...property} />}
          <PropertyImagesViewer images={property?.images || []} />
          <PropertyDescriptionViewer
            description={property?.description || ""}
          />
          <PropertyAdditionalQuestionsViewer
            additionalQuestions={property?.additionalQuestions || []}
          />
          <BookingConfigViewer bookingConfig={property?.bookingConfig || {}} />
          <PropertyPricingViewer
            currency={property?.pricing?.currency || ""}
            price={
              (property?.pricing?.price && String(property?.pricing?.price)) ||
              ""
            }
            pricingType={property?.pricing?.priceType || "MONTHLY"}
          />
        </Flex>
      </Flex>
    );
  };

  return (
    <StyledChangeLogContainer>
      <div
        style={{
          border: "1px solid #dbdbdb",
          borderRadius: "20px",
        }}
      >
        <Typography
          color="dark.neutral_1b"
          style={{ margin: "8px 0px 8px 8px" }}
        >
          {humanReadableDate(date, "DD MMM, YYYY, hh:mm:ss A")}
        </Typography>
        <div
          style={{
            backgroundColor: "rgba(200,130,234,0.2)",
            padding: "14px",
            borderRadius: "20px",
          }}
        >
          <Typography size={"title"} color="primary">
            {title}
          </Typography>
          {isAmenities && <AmenitiesViewer amenities={changes?.amenities} />}
          {isRefImageGroups && (
            <GroupImageViewer refImageGroups={changes?.refImageGroups} />
          )}
          {isRules && (
            <RulesViewer
              rules={changes?.rules?.map((r: IRule) => {
                const category = changeLog?.changes2?.rulesCategory;
                return {
                  icon: category?.icon,
                  category: category?.title,
                  rule: r.title,
                  description: r.description,
                };
              })}
            />
          )}
          {isInventory && <InventoryViewer inventories={changes.inventory} />}
          {isInventoryCategory && (
            <InventoryCategoryViewer
              inventoryCategories={changes.inventoryCategory?.map(
                (i: IInventoryCategoryType) => {
                  const category = changeLog.changes2.inventoryCategoryIcons;
                  return {
                    ...i,
                    icon: Array.isArray(category)
                      ? category?.find((y) => y._id === i.icon)?.icon
                      : category?.icon,
                  };
                }
              )}
            />
          )}
          {isUtility && (
            <UtilityProviderViewer
              utilityProviders={[
                {
                  ...changes.utilityProvider,
                  category: utilityCategory.category,
                  categoryIcon: utilityCategory.icon,
                },
              ]}
            />
          )}
          {isImages && <PropertyImagesViewer images={changes.images} />}
          {isDescription && (
            <PropertyDescriptionViewer description={changes.description} />
          )}
          {isAdditionalQuestions && (
            <PropertyAdditionalQuestionsViewer
              additionalQuestions={changes.additionalQuestions}
            />
          )}
          {isBookingConfig && (
            <BookingConfigViewer bookingConfig={changes.bookingConfig} />
          )}
          {isPricing && <PropertyPricingViewer {...changes.pricing} />}
          {isBasicInformation && (
            <PropertyBasicInformationViewer {...changes} />
          )}
          {isSnapshot && renderSnapShot()}
        </div>
      </div>
    </StyledChangeLogContainer>
  );
};
const StyledChangeLogContainer = styled.div`
  padding-left: 50px;
  padding-bottom: 24px;
  position: relative;
  &::before {
    content: "";
    position: absolute;
    left: 20px;
    top: 20px;
    height: 100%;
    width: 2px;
    background-color: #dbdbdb;
  }
  &::after {
    content: "";
    position: absolute;
    left: 20px;
    top: 20px;
    width: 30px;
    height: 2px;
    background-color: #dbdbdb;
  }
`;
